import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { exportToPDF, exportToExcel } from '../../utils/exportUtils';
import './Payments.css';

const Payments = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { getStudentPayments, calculatePaymentSummary } = useData();

  const userId = user?.id || user?._id;
  const payments = getStudentPayments(userId);
  const summary = calculatePaymentSummary(userId);

  const formatCurrency = (amount) => {
    const locale = i18n.language === 'nl' ? 'nl-NL' : 'en-US';
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }).format(Number(amount || 0));
  };

  const handleExportPDF = () => {
    const exportData = payments.map((p) => ({
      [t('payment.exportHeaders.course')]: p.course?.name || '-',
      [t('payment.exportHeaders.amount')]: p.amount,
      [t('payment.exportHeaders.status')]: p.status,
      [t('payment.exportHeaders.dueDate')]: p.dueDate ? new Date(p.dueDate).toLocaleDateString() : '-',
      [t('payment.exportHeaders.paidAt')]: p.paymentDate ? new Date(p.paymentDate).toLocaleDateString() : '-',
      [t('payment.exportHeaders.method')]: p.paymentMethod || '-'
    }));
    exportToPDF(exportData, `NLBA_Payments_${user?.fullName || 'student'}`, t('nav.payments'));
  };

  const handleExportExcel = () => {
    const exportData = payments.map((p) => ({
      [t('payment.exportHeaders.course')]: p.course?.name || '-',
      [t('payment.exportHeaders.amount')]: p.amount,
      [t('payment.exportHeaders.status')]: p.status,
      [t('payment.exportHeaders.dueDate')]: p.dueDate ? new Date(p.dueDate).toLocaleDateString() : '-',
      [t('payment.exportHeaders.paidAt')]: p.paymentDate ? new Date(p.paymentDate).toLocaleDateString() : '-',
      [t('payment.exportHeaders.method')]: p.paymentMethod || '-'
    }));
    exportToExcel(exportData, `NLBA_Payments_${user?.fullName || 'student'}`, t('payment.payments'), t('nav.payments'));
  };

  return (
    <div className="payments-page">
      <div className="page-header">
        <h1>{t('nav.payments')}</h1>
        {payments.length > 0 && (
          <div className="export-buttons">
            <button className="btn-primary" onClick={handleExportPDF}>
              {t('report.exportPDF')}
            </button>
            <button className="btn-primary" onClick={handleExportExcel}>
              {t('report.exportExcel')}
            </button>
          </div>
        )}
      </div>

      {/* Payment Summary */}
      <div className="payment-summary">
        <div className="summary-card total">
          <div className="summary-icon">💰</div>
          <div className="summary-content">
            <span className="summary-label">{t('payment.totalDue')}</span>
            <span className="summary-value">{formatCurrency(summary.totalDue)}</span>
          </div>
        </div>
        <div className="summary-card paid">
          <div className="summary-icon">✅</div>
          <div className="summary-content">
            <span className="summary-label">{t('payment.totalPaid')}</span>
            <span className="summary-value">{formatCurrency(summary.totalPaid)}</span>
          </div>
        </div>
        <div className="summary-card pending">
          <div className="summary-icon">⏳</div>
          <div className="summary-content">
            <span className="summary-label">{t('payment.pending')}</span>
            <span className="summary-value">{formatCurrency(summary.pending)}</span>
          </div>
        </div>
      </div>

      {/* Study Year Payments */}
      <div className="payments-list">
        <h2>{t('payment.paymentOverview')}</h2>
        
        {payments.length === 0 ? (
          <div className="no-payments">
            <p>{t('payment.noPayments')}</p>
          </div>
        ) : (
          payments.map((payment) => {
            const progress = payment.status === 'paid' ? 100 : 0;

            return (
              <div key={payment.id} className="payment-card">
                <div className="payment-header">
                  <div className="payment-study">
                    <h3>{payment.course?.name || '-'}</h3>
                    <span className={`payment-status status-${payment.status}`}>
                      {t(`payment.${payment.status}`)}
                    </span>
                  </div>
                  <span className="payment-type">{payment.paymentMethod || '-'}</span>
                </div>

                <div className="payment-amounts">
                  <div className="amount-row">
                    <span className="amount-label">{t('payment.totalAmount')}:</span>
                    <span className="amount-value">{formatCurrency(payment.amount)}</span>
                  </div>
                  <div className="amount-row">
                    <span className="amount-label">{t('payment.paid')}:</span>
                    <span className="amount-value paid">{formatCurrency(payment.status === 'paid' ? payment.amount : 0)}</span>
                  </div>
                  <div className="amount-row">
                    <span className="amount-label">{t('payment.remaining')}:</span>
                    <span className="amount-value remaining">{formatCurrency(payment.status === 'paid' ? 0 : payment.amount)}</span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="payment-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">{Math.round(progress)}% {t('payment.completed')}</span>
                </div>

                {/* Due Date */}
                {payment.dueDate && (
                  <div className="payment-due-date">
                    <span className="due-label">{t('payment.dueDate')}:</span>
                    <span className={`due-value ${new Date(payment.dueDate) < new Date() ? 'overdue' : ''}`}>
                      {payment.dueDate}
                    </span>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Payments;
